/**
 * budget.js — 50/30/20 budget calculation engine
 *
 * Calculates budget targets and actuals based on the 50/30/20 framework:
 * - 50% Needs (essential/fixed expenses)
 * - 30% Wants (discretionary spending)
 * - 20% Savings (emergency fund, retirement, debt paydown)
 *
 * Budget targets are calculated against conservative income ($8,030/mo)
 * to provide a safe baseline. Actual income above that is treated as
 * a savings/debt-payoff opportunity.
 */

import theme from './theme';

/**
 * Budget bucket identifiers.
 */
export const BUCKETS = {
  NEED: 'need',
  WANT: 'want',
  SAVINGS: 'savings',
  INCOME: 'income',
};

/**
 * Target percentages for each bucket.
 */
export const BUCKET_TARGETS = {
  [BUCKETS.NEED]: 0.50,
  [BUCKETS.WANT]: 0.30,
  [BUCKETS.SAVINGS]: 0.20,
};

/**
 * Display metadata for each bucket.
 */
export const BUCKET_META = {
  [BUCKETS.NEED]: { label: 'Needs', emoji: '🏠', color: theme.bucketNeed },
  [BUCKETS.WANT]: { label: 'Wants', emoji: '🛍️', color: theme.bucketWant },
  [BUCKETS.SAVINGS]: { label: 'Savings', emoji: '💰', color: theme.bucketSavings },
};

/**
 * Calculate 50/30/20 budget breakdown for a given month.
 *
 * @param {Object} params
 * @param {number} params.income - Monthly income to budget against (e.g., 8030)
 * @param {number} params.needActual - Actual Need spending this month
 * @param {number} params.wantActual - Actual Want spending this month
 * @param {number} params.savingsActual - Actual Savings this month
 * @returns {Object} Budget breakdown with targets, actuals, and status for each bucket
 */
export function calculateBudget({ income, needActual = 0, wantActual = 0, savingsActual = 0 }) {
  const needTarget = income * BUCKET_TARGETS[BUCKETS.NEED];
  const wantTarget = income * BUCKET_TARGETS[BUCKETS.WANT];
  const savingsTarget = income * BUCKET_TARGETS[BUCKETS.SAVINGS];

  return {
    income,
    buckets: {
      [BUCKETS.NEED]: {
        target: needTarget,
        actual: needActual,
        remaining: needTarget - needActual,
        percent: needTarget > 0 ? (needActual / needTarget) * 100 : 0,
        status: getStatus(needActual, needTarget),
      },
      [BUCKETS.WANT]: {
        target: wantTarget,
        actual: wantActual,
        remaining: wantTarget - wantActual,
        percent: wantTarget > 0 ? (wantActual / wantTarget) * 100 : 0,
        status: getStatus(wantActual, wantTarget),
      },
      [BUCKETS.SAVINGS]: {
        target: savingsTarget,
        actual: savingsActual,
        remaining: savingsTarget - savingsActual,
        // For savings, higher is better — invert the logic
        percent: savingsTarget > 0 ? (savingsActual / savingsTarget) * 100 : 0,
        status: getSavingsStatus(savingsActual, savingsTarget),
      },
    },
    totalSpent: needActual + wantActual,
    totalBudget: income,
    overallPercent: income > 0 ? ((needActual + wantActual) / income) * 100 : 0,
  };
}

/**
 * Get status classification for a spending bucket (Need/Want).
 * Under budget = on-track (green), slightly over = warning (yellow), way over = over (red).
 *
 * @param {number} actual - Actual spending
 * @param {number} target - Budget target
 * @returns {'on-track'|'warning'|'over'} Status
 */
function getStatus(actual, target) {
  if (target <= 0) return 'on-track';
  const ratio = actual / target;
  if (ratio <= 1.0) return 'on-track';
  if (ratio <= 1.15) return 'warning';
  return 'over';
}

/**
 * Get status classification for savings (inverted — more is better).
 * @param {number} actual - Actual savings
 * @param {number} target - Savings target
 * @returns {'on-track'|'warning'|'over'} Status
 */
function getSavingsStatus(actual, target) {
  if (target <= 0) return 'on-track';
  const ratio = actual / target;
  if (ratio >= 0.8) return 'on-track';
  if (ratio >= 0.4) return 'warning';
  return 'over'; // 'over' here means dangerously under-saving
}

/**
 * Default category-to-bucket mapping.
 * Seeded from financial-baseline.md "Category → Budget Bucket Mapping" section.
 * Users can override these in the BudgetPage category mapper.
 *
 * Keys are Monarch Money category names (case-insensitive match).
 */
export const DEFAULT_CATEGORY_MAP = {
  // Needs
  'Mortgage': BUCKETS.NEED,
  'Auto Payment': BUCKETS.NEED,
  'Auto Maintenance': BUCKETS.NEED,
  'Gas': BUCKETS.NEED,
  'Gas & Electric': BUCKETS.NEED,
  'Internet & Cable': BUCKETS.NEED,
  'Security System': BUCKETS.NEED,
  'Phone': BUCKETS.NEED,
  'Groceries': BUCKETS.NEED,
  'Insurance': BUCKETS.NEED,
  'Medical': BUCKETS.NEED,
  'Student Loans': BUCKETS.NEED,
  'Taxes': BUCKETS.NEED,
  'Education': BUCKETS.NEED,
  'Home Payment': BUCKETS.NEED,

  // Wants
  'Shopping': BUCKETS.WANT,
  'Restaurants & Bars': BUCKETS.WANT,
  'Coffee Shops': BUCKETS.WANT,
  'Travel & Vacation': BUCKETS.WANT,
  'Entertainment': BUCKETS.WANT,
  'Entertainment & Recreation': BUCKETS.WANT,
  'Home Improvement': BUCKETS.WANT,
  'Furniture & Housewares': BUCKETS.WANT,
  'Clothing': BUCKETS.WANT,
  'Electronics': BUCKETS.WANT,
  'Gifts': BUCKETS.WANT,
  'Charity': BUCKETS.WANT,
  'Child Activities': BUCKETS.WANT,
  'Pets': BUCKETS.WANT,
  'Personal': BUCKETS.WANT,
  'Fitness': BUCKETS.WANT,
  'Health & Wellness': BUCKETS.WANT,
  'Financial Fees': BUCKETS.WANT,
  'Cash & ATM': BUCKETS.WANT,
  'Miscellaneous': BUCKETS.WANT,
  'Uncategorized': BUCKETS.WANT,
  'Postage & Shipping': BUCKETS.WANT,
  'Parking & Tolls': BUCKETS.WANT,

  // Phase 18 taxonomy labels
  'mortgage / rent':   BUCKETS.NEED,
  'home services':     BUCKETS.NEED,
  'phone & internet':  BUCKETS.NEED,
  'medical & dental':  BUCKETS.NEED,
  'general shopping':  BUCKETS.WANT,
  'ride shares':       BUCKETS.WANT,
  'gifts & charity':   BUCKETS.WANT,
  'debt payment':      BUCKETS.NEED,
  'savings transfer':  BUCKETS.SAVINGS,
  'fees & interest':   BUCKETS.WANT,
};

/**
 * Look up the budget bucket for a Monarch category.
 * Falls back to "want" for unknown categories (conservative — assumes discretionary).
 *
 * @param {string} category - Monarch category name
 * @param {Object} [overrides] - User-defined category overrides from Firestore
 * @returns {string} Bucket identifier ('need', 'want', or 'savings')
 */
export function getCategoryBucket(category, overrides = {}) {
  if (!category) return BUCKETS.WANT;

  // Check user overrides first (case-insensitive)
  const lowerCategory = category.toLowerCase();
  for (const [key, bucket] of Object.entries(overrides)) {
    if (key.toLowerCase() === lowerCategory) return bucket;
  }

  // Check defaults (case-insensitive)
  for (const [key, bucket] of Object.entries(DEFAULT_CATEGORY_MAP)) {
    if (key.toLowerCase() === lowerCategory) return bucket;
  }

  // Unknown defaults to Want (conservative)
  return BUCKETS.WANT;
}
